import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Search, User, LogOut, Smartphone } from 'lucide-react';
import SearchAutocomplete from '@/components/SearchAutocomplete';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import CurrencySwitcher from '@/components/CurrencySwitcher';
import { useLang } from '@/lib/i18n';
import { lv } from '@/components/MultiLangField';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [settings, setSettings] = useState({});
  const [user, setUser] = useState(null);
  const location = useLocation();
  const { t, lang } = useLang();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetch(`${API}/api/settings`).then(r => r.json()).then(setSettings).catch(() => {});
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    const data = localStorage.getItem('user_data');
    setUser(data ? JSON.parse(data) : null);
  }, [location]);

  // React to login/logout in other tabs so the header stays in sync without reload.
  useEffect(() => {
    const sync = (e) => {
      if (e.key && e.key !== 'user_data' && e.key !== 'user_token') return;
      const data = localStorage.getItem('user_data');
      const token = localStorage.getItem('user_token');
      setUser(data && token ? JSON.parse(data) : null);
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') setSearchOpen(false);
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSearchOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const sections = settings.sections || {};
  const navLinks = [
    { to: '/modelki', label: t('nav.catalog') },
    ...(sections.faq !== false ? [{ to: '/faq', label: t('nav.faq') }] : []),
    ...(sections.services !== false ? [{ to: '/uslugi', label: t('nav.services') }] : []),
    ...(sections.about !== false ? [{ to: '/o-nas', label: t('nav.about') }] : []),
    ...(sections.searchByParams !== false ? [{ to: '/szukaj', label: t('nav.search') }] : []),
    ...(sections.escortWork !== false ? [{ to: '/praca', label: t('nav.work') }] : []),
  ];

  const logout = () => {
    localStorage.removeItem('user_token');
    localStorage.removeItem('user_data');
    window.location.href = '/';
  };

  return (
    <>
      {settings.bannerEnabled && lv(settings.bannerText, lang) && (
        <div data-testid="site-banner" className="fixed top-0 left-0 right-0 z-[60] bg-[var(--color-primary)] text-white text-center text-xs sm:text-sm py-1.5 px-4">
          <span dangerouslySetInnerHTML={{ __html: lv(settings.bannerText, lang) }} />
        </div>
      )}
      <header className={`modern-header fixed left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'is-scrolled' : ''}`}
        style={{ top: settings.bannerEnabled && lv(settings.bannerText, lang) ? '28px' : '0' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="header-logo" className="brand-lockup" aria-label={settings.siteName || 'SecretGirls Escort'}>
            <span className="brand-monogram" aria-hidden="true">
              <span>S</span><span>G</span>
            </span>
            <span className="brand-wordmark">
              <strong>SecretGirls</strong>
              <small>Escort</small>
            </span>
          </Link>

          <nav className="modern-nav hidden lg:flex items-center gap-1 rounded-full p-1.5">
            {navLinks.map(link => (
              <Link key={link.to} to={link.to} data-testid={`nav-${link.to.slice(1)}`}
                className={`px-4 py-2 rounded-full text-[13px] transition-colors ${
                  location.pathname === link.to || location.pathname.startsWith(link.to + '/')
                    ? 'text-white bg-white/10' : 'text-white/55 hover:text-white hover:bg-white/5'}`}>
                {link.label}
              </Link>
            ))}
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                window.dispatchEvent(new CustomEvent('open-pwa-install'));
              }}
              className="flex items-center gap-2 text-sm font-semibold text-rose-400 bg-rose-500/10 border border-rose-500/20 px-4 py-2 rounded-full my-1 hover:bg-rose-500/20 transition-colors"
            >
              <Smartphone className="w-4 h-4" />
              <span>
                {lang === 'pl' ? 'Pobierz aplikację' : lang === 'ru' ? 'Скачать приложение' : lang === 'ua' ? 'Завантажити додаток' : 'Install App'}
              </span>
            </button>
          </nav>

          <div className="flex items-center gap-2">
            <CurrencySwitcher />
            <LanguageSwitcher />

            <button data-testid="header-search-toggle" onClick={() => setSearchOpen(true)}
              className="modern-icon-button w-10 h-10 rounded-full flex items-center justify-center text-white/60 hover:text-white transition-colors">
              <Search className="w-4 h-4" />
            </button>

            {user ? (
              <div className="hidden sm:flex items-center gap-2">
                <Link to="/konto" data-testid="header-cabinet"
                  className="flex items-center gap-2 bg-[var(--color-primary)]/10 border border-[var(--color-primary)]/20 rounded-full px-3 py-1.5 text-sm text-[var(--color-primary)] hover:bg-[var(--color-primary)]/20 transition-colors">
                  <User className="w-3.5 h-3.5" /> {user.username}
                </Link>
                <button onClick={logout} data-testid="header-logout"
                  className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center text-white/40 hover:text-red-400 transition-colors">
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link to="/logowanie" data-testid="header-login"
                  className="px-3 py-1.5 rounded-full text-sm text-white/60 hover:text-white transition-colors">
                  {t('nav.login')}
                </Link>
                <Link to="/rejestracja" data-testid="header-register"
                  className="modern-header-cta px-5 py-2.5 rounded-full text-sm text-white">
                  {t('nav.register')}
                </Link>
              </div>
            )}

            <button data-testid="mobile-menu-toggle" onClick={() => setMenuOpen(!menuOpen)}
              className="modern-icon-button lg:hidden w-10 h-10 rounded-full flex items-center justify-center text-white/70">
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-40 bg-black/95 backdrop-blur-xl pt-20 lg:hidden">
          <nav className="flex flex-col items-center gap-4 pt-8 px-4">
            {navLinks.map(link => (
              <Link key={link.to} to={link.to} data-testid={`mobile-nav-${link.to.slice(1)}`}
                className="text-lg font-medium text-white/80 hover:text-[var(--color-primary)] transition-colors">
                {link.label}
              </Link>
            ))}
            <hr className="w-24 border-white/10 my-2" />
            {user ? (
              <>
                <Link to="/konto" className="text-lg text-[var(--color-primary)]">{user.username}</Link>
                <button onClick={logout} className="text-sm text-red-400">{t('nav.logout')}</button>
              </>
            ) : (
              <>
                <Link to="/logowanie" className="text-lg text-white/60">{t('nav.login')}</Link>
                <Link to="/rejestracja" className="text-lg text-[var(--color-primary)]">{t('nav.register')}</Link>
              </>
            )}
          </nav>
        </div>
      )}

      {searchOpen && <SearchAutocomplete onClose={() => setSearchOpen(false)} />}
    </>
  );
}
